import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './FeatureTabs.module.scss';
export interface FeatureTabsItem {
  /** A stable key for the item; `defaultOpen` names it. */
  id: string;
  /** The disclosure's label: the button's text and accessible name. */
  title: ReactNode;
  /** The open item's copy. A closed item's body is not rendered. */
  body: ReactNode;
  /** Shown above the open item's body below `lg` (an image with its alt). */
  media?: ReactNode;
  /**
   * Shown in the side panel from `lg` while the item is open. Defaults to
   * `media`; pass both when the two images carry different alt text.
   */
  panelMedia?: ReactNode;
}
export type FeatureTabsTitleAs = 'h2' | 'h3' | 'h4' | 'none';

/**
 * The titles' type. `question` (the default): FAQAccordion compact's
 * question (17 / 600 / 1.4), as on the program pages, Experience and Try
 * College. `headline` (O235, design batch 3): the card title role
 * (`display-card`: 28 / 1.05 / 600 / −0.035em) at every width, for toggles
 * whose titles are headlines (Employers' "How it works", the legacy 28px
 * titles).
 */
export type FeatureTabsTitleSize = 'question' | 'headline';
export interface FeatureTabsProps {
  /** The group's accessible name. */
  label: string;
  items: readonly FeatureTabsItem[];
  /**
   * The item open on first render: the first item by default; `null` opens
   * none.
   */
  defaultOpen?: string | null;
  /**
   * `none` (the default) renders each button on its own; `h2`, `h3` or `h4`
   * puts each button inside a heading of that level (Forever 21's toggles are
   * `h2`s: PR 5.7).
   */
  titleAs?: FeatureTabsTitleAs;
  /** The titles' type: `question` (default) or `headline`. */
  titleSize?: FeatureTabsTitleSize;
}

/**
 * A list of disclosures, one open at a time, the first open by default
 * (Phase 5 PR 5.0, Q21; the legacy "Student experience" toggles). Each title
 * is a `<button aria-expanded>`; activating the open item closes it, and a
 * closed item's body is not rendered. Below `lg` the open item shows its
 * media above its body; from `lg` the list sits beside a panel that shows
 * the open item's `panelMedia`, and the inline media is hidden, so each
 * width shows one copy of the image. Not a tablist: no arrow-key roving and
 * no `role="tab"`. Incubating: no page adopts it in 5.0.
 */
function FeatureTabs(t0) {
  const $ = _c(26);
  const {
    label,
    items,
    defaultOpen,
    titleAs: t1,
    titleSize: t2
  } = t0;
  const titleAs = t1 === undefined ? "none" : t1;
  const titleSize = t2 === undefined ? "question" : t2;
  const [openId, setOpenId] = useState(defaultOpen === undefined ? items[0]?.id ?? null : defaultOpen);
  const baseId = useId();
  if (items.length === 0) {
    return null;
  }
  let t3;
  if ($[0] !== items || $[1] !== openId) {
    let t4;
    if ($[3] !== openId) {
      t4 = item => item.id === openId;
      $[3] = openId;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = items.find(t4);
    $[0] = items;
    $[1] = openId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const openItem = t3;
  const panelMedia = openItem ? openItem.panelMedia ?? openItem.media : null;
  const Heading = titleAs === "none" ? null : titleAs;
  let t4;
  if ($[5] !== Heading || $[6] !== baseId || $[7] !== items || $[8] !== openId || $[9] !== titleSize) {
    let t5;
    if ($[11] !== Heading || $[12] !== baseId || $[13] !== openId || $[14] !== titleSize) {
      t5 = (item_0, index) => {
        const open = item_0.id === openId;
        const buttonId = `${baseId}-button-${index}`;
        const bodyId = `${baseId}-body-${index}`;
        const button = <button aria-controls={open ? bodyId : undefined} aria-expanded={open} className={classNames(styles["helios-feature-tabs__trigger"], {
          [styles["helios-feature-tabs__trigger--headline"]]: titleSize === "headline",
          [styles["helios-feature-tabs__trigger--open"]]: open
        })} id={buttonId} onClick={() => setOpenId(current => current === item_0.id ? null : item_0.id)} type="button">{item_0.title}</button>;
        return <div className={styles["helios-feature-tabs__item"]} data-helios-feature-tab="" data-open={open ? "true" : "false"} key={item_0.id}>{Heading ? <Heading className={styles["helios-feature-tabs__heading"]}>{button}</Heading> : button}{open ? <div className={styles["helios-feature-tabs__body"]} id={bodyId}>{item_0.media ? <div className={classNames(styles["helios-feature-tabs__media"], styles["helios-feature-tabs__media--inline"])}>{item_0.media}</div> : null}<div className={styles["helios-feature-tabs__copy"]}>{item_0.body}</div></div> : null}</div>;
      };
      $[11] = Heading;
      $[12] = baseId;
      $[13] = openId;
      $[14] = titleSize;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = items.map(t5);
    $[5] = Heading;
    $[6] = baseId;
    $[7] = items;
    $[8] = openId;
    $[9] = titleSize;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <div className={styles["helios-feature-tabs__list"]}>{t4}</div>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== panelMedia) {
    t6 = panelMedia ? <div className={classNames(styles["helios-feature-tabs__media"], styles["helios-feature-tabs__media--panel"])}>{panelMedia}</div> : null;
    $[18] = panelMedia;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <div className={styles["helios-feature-tabs__panel"]}>{t6}</div>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== label || $[23] !== t5 || $[24] !== t7) {
    t8 = <div aria-label={label} className={styles["helios-feature-tabs"]} data-helios-feature-tabs="" role="group">{t5}{t7}</div>;
    $[22] = label;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
export default FeatureTabs;